/**
 * SeparationRunChip.tsx
 *
 * The on-page trace of a voice/music separation running in the background, the
 * twin of `DubRunChip`. A dub with "Keep Original Music" ticked starts the split
 * alongside the dub and the two finish in either order — so once the dub's chip
 * is gone, this is what says the music is still on its way.
 *
 * The run lives in `DubbingStore` (`startSeparation`); renders nothing while no
 * separation for this story is in flight.
 */
import { useEstimatedProgress } from '@src/components/common/useEstimatedProgress';
import { RunChip } from '@src/components/transcript/RunChip';
import { estimateStemSeparationMs } from '@src/config/processingEstimates';
import { useDubbingStore, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';

export const SeparationRunChip = observer(function SeparationRunChip() {
  const rootStore = useStore();
  const dubbingStore = useDubbingStore();
  const videoCreator = useVideoCreatorStore();
  // Per-story, for the reason the dub chip is.
  const activeRun = dubbingStore?.separationRun ?? null;
  const run = activeRun?.storyId === rootStore.storyId ? activeRun : null;
  // The same estimate the language menu's Music row runs, anchored to the same
  // start, so the two agree.
  const progress = useEstimatedProgress(
    run !== null,
    estimateStemSeparationMs({
      videoDurationSec: videoCreator.story?.originalVideo?.video?.duration,
    }),
    { startedAt: run?.startedAt ?? null },
  );
  if (!run) return null;

  const percent = Math.round(progress);

  return (
    <RunChip
      percent={percent}
      code="Music"
      ariaLabel={`Separating the original music, ${percent}%`}
      tooltip={`Separating the original music — ${percent}%`}
      cancelLabel="Stop waiting for music separation"
      cancelTooltip="Stop Waiting"
      onCancel={() => dubbingStore.cancelSeparation()}
    />
  );
});
